import React, { useState, useEffect } from "react"; // Import useEffect
import { set, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUser } from "../../../context/UserContext";
import httpServices from "../../../services/httpServices";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../component/common/overlay";
import GoogleMapComponent from "../../../services/googleMapLocation";

const validationSchema = Yup.object().shape({
  venueAddress: Yup.string()
    .required("Venue address is required")
    .min(10, "Venue address must be at least 10 characters"),
  venueAddressMapUrl: Yup.string()
    .url("Please enter a valid URL")
    .required("Venue URL is required"),
  venueDescription: Yup.string()
    .required("Venue description is required")
    .min(50, "Venue description must be at least 50 characters")
    .max(500, "Venue description must be less than 500 characters"),
  venueCountry: Yup.string().required("Country is required"),
  venueState: Yup.string().required("State is required"),
  venueCity: Yup.string().required("City is required"),
  venueZipCode: Yup.string().required("Zipcode is required"),
});

function VenueAddress({ setActiveStep, venueId, venueData }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState("Pune,India");
  const [addressDetails, setAddressDetails] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      venueAddress: venueData?.venueAddress,
      venueAddressMapUrl: venueData?.venueAddressMapUrl,
      venueDescription: venueData?.venueDescription,
      venueCountry: venueData?.venueCountry,
      venueState: venueData?.venueState,
      venueCity: venueData?.venueCity,
      venueZipCode: venueData?.venueZipCode,
    },
  });

  // Function to handle form submission
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const { response, error } = await httpServices.put(
        `/venue/update/${venueId}`,
        {
          ...data,
          stepsDone: 3,
          userId: user?.userId,
        }
      );

      if (error) {
        throw error;
      }

      if (response.status === 200) {
        toast.success("Venue address saved successfully");
        setActiveStep(3);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onError = (error) => {
    if (venueData === null || venueData?.stepsDone < 2) {
      toast.warn("Please complete the previous step");
      clearErrors();
    }
  };

  const handleAddressChange = (newAddressDetails) => {
    setValue("venueCity", newAddressDetails?.city);
    setValue("venueCountry", newAddressDetails?.country);
    setValue("venueState", newAddressDetails?.state);
    setValue("venueZipCode", newAddressDetails?.postalCode);
    setValue("venueAddressMapUrl", newAddressDetails?.googleMapUrl);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="mt-3 p-3 bg-white relative">
          {loading && <LoadingOverlay />}
          <div className="row">
            <div className="col-lg-7 col-12">
              <div>
                <h3 className="text-blue fw-semibold text-xl mb-4">
                  Venue Address
                </h3>
                {/* Venue Address */}
                <div className="mb-3">
                  <label className="font-medium text-base mb-2">
                    Where’s the venue?*
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Venue Address"
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueAddress ? "border-red-500" : ""
                    }`}
                    {...register("venueAddress")}
                    onChange={(e) => {
                      setLocation(e.target.value);
                      // handleAddressSelect();
                    }}
                  />
                  {errors.venueAddress && (
                    <p className="text-red-500 text-sm">
                      {errors.venueAddress.message}
                    </p>
                  )}
                  <GoogleMapComponent
                    relativeLocation={location}
                    onAddressChange={handleAddressChange}
                  />
                </div>
                {/* Venue URL */}
                <div className="mb-3">
                  <label className="font-medium text-base mb-2">
                    Add venue URL*
                  </label>
                  <textarea
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueAddressMapUrl ? "border-red-500" : ""
                    }`}
                    placeholder="Add venue URL"
                    {...register("venueAddressMapUrl")}
                    rows="5"
                    cols="30"
                  ></textarea>
                  {errors.venueAddressMapUrl && (
                    <p className="text-red-500 text-sm">
                      {errors.venueAddressMapUrl.message}
                    </p>
                  )}
                </div>
                {/* Venue Description */}
                <div className="mb-3">
                  <label className="font-medium text-base mb-2">
                    Venue Description*
                  </label>
                  <textarea
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueDescription ? "border-red-500" : ""
                    }`}
                    placeholder="Add venue description of up to 50-500 characters"
                    {...register("venueDescription")}
                    rows="10"
                    cols="30"
                  ></textarea>
                  {errors.venueDescription && (
                    <p className="text-red-500 text-sm">
                      {errors.venueDescription.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Address Details */}
            <div className="col-lg-5 col-12">
              <div className="row row-cols-2 mt-5">
                {/* Country */}
                <div className="col mb-3">
                  <label className="font-medium text-base mb-2">Country</label>
                  <input
                    type="text"
                    placeholder="Enter Country"
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueCountry ? "border-red-500" : ""
                    }`}
                    {...register("venueCountry")}
                  />
                  {errors.venueCountry && (
                    <p className="text-red-500 text-sm">
                      {errors.venueCountry.message}
                    </p>
                  )}
                </div>
                {/* State */}
                <div className="col mb-3">
                  <label className="font-medium text-base mb-2">State</label>
                  <input
                    type="text"
                    placeholder="Enter State"
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueState ? "border-red-500" : ""
                    }`}
                    {...register("venueState")}
                  />
                  {errors.venueState && (
                    <p className="text-red-500 text-sm">
                      {errors.venueState.message}
                    </p>
                  )}
                </div>
                {/* City */}
                <div className="col mb-3">
                  <label className="font-medium text-base mb-2">City</label>
                  <input
                    type="text"
                    placeholder="Enter City"
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueCity ? "border-red-500" : ""
                    }`}
                    {...register("venueCity")}
                  />
                  {errors.venueCity && (
                    <p className="text-red-500 text-sm">
                      {errors.venueCity.message}
                    </p>
                  )}
                </div>
                {/* Zipcode */}
                <div className="col mb-3">
                  <label className="font-medium text-base mb-2">Zipcode</label>
                  <input
                    type="text"
                    placeholder="Enter Zipcode"
                    className={`custom-input bg-[#F5F6FA] ${
                      errors.venueZipCode ? "border-red-500" : ""
                    }`}
                    {...register("venueZipCode")}
                  />
                  {errors.venueZipCode && (
                    <p className="text-red-500 text-sm">
                      {errors.venueZipCode.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={() => setActiveStep(1)}
          >
            Back
          </button>
          <button type="submit" className="btn btn-primary mt-3">
            Next
          </button>
        </div>
      </form>
    </>
  );
}

export default VenueAddress;
